import { inject, Injectable } from '@angular/core';
import { RouterLinkFactory } from '../../core/router/router-link-factory';
import { IconsService } from '@rieaya-core';
import { translateSignal } from '@jsverse/transloco';
import { PERMISSIONS_KEYS } from '../../core/utils/permissions';
import { environment } from '../../../environments/environment';
import { ADMIN, ADMIN_STAFF, USER_DATA } from '../../core/utils/constant';
import { signal } from '@angular/core';
import { User } from '../../core/interfaces/user';

export interface MenuItemStructure {
  label: string;
  icon: string;
  href?: string;
  expanded?: boolean;
  permissionId?: string;
  children?: MenuItemStructure[];
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private routerLinks = inject(RouterLinkFactory);
  private icons = inject(IconsService);

  translations = {
    dashboard: translateSignal('shared/dashboard'),
    benefitsAndCompanies: translateSignal('shared/benefitsAndCompanies'),
    contentAndSupport: translateSignal('shared/contentAndSupport'),
    administration: translateSignal('shared/administration'),
    companiesOffers: translateSignal('shared/companiesOffers'),
    offersUnderReview: translateSignal('shared/offersUnderReview'),
    companies: translateSignal('shared/companies'),
    blockedRegistrations: translateSignal('companies/blockedRegistrations'),
    beneficiaries: translateSignal('shared/beneficiaries'),
    affiliates: translateSignal('shared/affiliates'),
    relatives: translateSignal('shared/relatives'),
    delegates: translateSignal('shared/delegates'),
    correspondences: translateSignal('shared/correspondences'),
    reports: translateSignal('shared/reports'),
    staffAndPermissions: translateSignal('shared/staffAndPermissions'),
    news: translateSignal('shared/news'),
    settings: translateSignal('shared/settings'),
    cities: translateSignal('shared/cities'),
    offerCategories: translateSignal('shared/offerCategories'),
    pages: translateSignal('shared/pages'),
    email: translateSignal('shared/email'),
    walletSettings: translateSignal('shared/walletSettings'),
    editWallet: translateSignal('shared/editWallet'),
    walletCycle: translateSignal('shared/walletCycle'),
    financialReport: translateSignal('shared/exportReport'),
    exportOperationsReport: translateSignal('shared/exportOperationsReport'),
    paymentOperationsReport: translateSignal('shared/paymentOperationsReport'),
    laundries: translateSignal('shared/laundries'),
    branches: translateSignal('shared/branches'),
    collectionPoints: translateSignal('shared/collectionPoints'),
    additionRequests: translateSignal('shared/additionRequests'),
    employees: translateSignal('shared/employees'),
    auditHistory: translateSignal('shared/auditHistory'),
    privileges: translateSignal('shared/privileges'),
    laundryCategories: translateSignal('shared/laundryCategories'),
    services: translateSignal('shared/services'),
    orderRules: translateSignal('shared/orderRules'),
    workingHours: translateSignal('shared/workingHours'),
    orders: translateSignal('shared/orders'),
    bills: translateSignal('shared/bills'),
    packages: translateSignal('shared/packages'),
    overviewPackages: translateSignal('shared/overviewPackages'),
    packagesSubscriptions: translateSignal('shared/packagesSubscriptions'),
    offersIncreases: translateSignal('shared/offersIncreases'),
    adminProcedures: translateSignal('shared/adminProcedures'),
    beneficiariesInfoWallet: translateSignal('shared/beneficiariesInfoWallet'),
    roles: translateSignal('shared/roles'),
    loans: translateSignal('shared/loans'),
    loanRequests: translateSignal('shared/loanRequests'),
    overviewLoanRequests: translateSignal('shared/overviewLoanRequests'),
    modificationExceptions: translateSignal('shared/modificationExceptions'),
    loanManagement: translateSignal('shared/loanManagement'),
    loanTypes: translateSignal('shared/loanTypes'),
    loanCategories: translateSignal('shared/loanCategories'),
    attachments: translateSignal('shared/attachments'),
    deductionEntities: translateSignal('shared/deductionEntities'),
    insurances: translateSignal('shared/insurances'),
    committees: translateSignal('shared/committees'),
    overviewCommittees: translateSignal('loans/overviewCommittees'),
    committeesMembers: translateSignal('shared/committeesMembers'),
    committeesRequests: translateSignal('shared/committeesRequests'),
    announcements: translateSignal('shared/announcements'),
    events: translateSignal('shared/events'),
    messageEntites: translateSignal('shared/messageEntites'),
    paymentMethods: translateSignal('shared/paymentMethods'),
    stores: translateSignal('stores/stores'),
    storeActivities: translateSignal('stores/storeActivities'),
    returnPolicy: translateSignal('stores/returnPolicy'),
  };

  isProduction = environment.production;

  userData = signal<User>(JSON.parse(localStorage.getItem(USER_DATA) || '{}'));

  getStructure(role?: string): MenuItemStructure[] {
    const userRole = role || this.userData().role;

    return [
      {
        label: this.translations.dashboard(),
        icon: this.icons.gauge,
        href: this.routerLinks.homepage()[0],
        permissionId: PERMISSIONS_KEYS.access_homepage,
      },
      {
        label: this.translations.benefitsAndCompanies(),
        icon: this.icons.gift,
        expanded: false,
        children: [
          {
            label: this.translations.companiesOffers(),
            icon: this.icons.tags,
            href: this.routerLinks.companyOffers()[0],
            permissionId: PERMISSIONS_KEYS.access_companyOffers,
          },
          {
            label: this.translations.companies(),
            icon: this.icons.building,
            href: this.routerLinks.companies()[0],
            permissionId: PERMISSIONS_KEYS.access_companies,
          },
          {
            label: this.translations.blockedRegistrations(),
            icon: this.icons.ban,
            href: this.routerLinks.blockedCompanyRegistrations()[0],
            permissionId: PERMISSIONS_KEYS.access_companies,
          },
          {
            label: this.translations.reports(),
            icon: this.icons.chartPie,
            href: this.routerLinks.reports()[0],
            permissionId: PERMISSIONS_KEYS.access_reports,
          },
        ],
      },
      {
        label: this.translations.beneficiaries(),
        icon: this.icons.users,
        expanded: false,
        children: [
          {
            label: this.translations.affiliates(),
            icon: this.icons.users,
            href: this.routerLinks.users()[0],
            permissionId: PERMISSIONS_KEYS.access_users,
          },
          {
            label: this.translations.relatives(),
            icon: this.icons.addressBook,
            href: this.routerLinks.relatives()[0],
            permissionId: PERMISSIONS_KEYS.access_relatives,
          },
          {
            label: this.translations.delegates(),
            icon: this.icons.cardID,
            href: this.routerLinks.delegates()[0],
            permissionId: PERMISSIONS_KEYS.access_delegates,
          },
        ],
      },
      {
        label: this.translations.laundries(),
        icon: this.icons.shop,
        expanded: false,
        children: [
          {
            label: this.translations.laundries(),
            icon: this.icons.shop,
            href: this.routerLinks.laundriesLaundries()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesLaundries,
          },
          {
            label: this.translations.branches(),
            icon: this.icons.sitemap,
            href: this.routerLinks.laundriesBranches()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesBranches,
          },
          {
            label: this.translations.collectionPoints(),
            icon: this.icons.mapMarker,
            href: this.routerLinks.laundriesCollectionPoints()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesCollectionPoints,
          },
          {
            label: this.translations.additionRequests(),
            icon: this.icons.filePlus,
            href: this.routerLinks.laundriesAdditionRequests()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesAdditionRequests,
          },
          {
            label: this.translations.employees(),
            icon: this.icons.users,
            href: this.routerLinks.laundriesEmployees()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesEmployees,
          },
          {
            label: this.translations.auditHistory(),
            icon: this.icons.history,
            href: this.routerLinks.laundriesAuditHistory()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesAuditHistory,
          },
          {
            label: this.translations.laundryCategories(),
            icon: this.icons.list,
            href: this.routerLinks.laundriesLaundryCategories()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesLaundryCategories,
          },
          {
            label: this.translations.services(),
            icon: this.icons.wrench,
            href: this.routerLinks.laundriesServices()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesServices,
          },
          {
            label: this.translations.orders(),
            icon: this.icons.shoppingCart,
            href: this.routerLinks.laundriesOrders()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesOrders,
          },
          {
            label: this.translations.bills(),
            icon: this.icons.receipt,
            href: this.routerLinks.laundriesBills()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesBills,
          },
          {
            label: this.translations.reports(),
            icon: this.icons.chartBar,
            href: this.routerLinks.laundriesReports()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesReports,
          },
          {
            label: this.translations.packages(),
            icon: this.icons.box,
            href: this.routerLinks.laundriesOverviewPackages()[0],
            permissionId: PERMISSIONS_KEYS.access_laundriesOverviewPackages,
          },
          {
            label: this.translations.packagesSubscriptions(),
            icon: this.icons.calendarClock,
            href: this.routerLinks.packagesSubscriptions()[0],
            permissionId: PERMISSIONS_KEYS.access_packagesSubscriptions,
          },
          {
            label: this.translations.offersIncreases(),
            icon: this.icons.percentage,
            href: this.routerLinks.packagesOffersIncreases()[0],
            permissionId: PERMISSIONS_KEYS.access_packagesOffersIncreases,
          },
          {
            label: this.translations.privileges(),
            icon: this.icons.shield,
            href: this.routerLinks.laundrySettingsPrivileges()[0],
            permissionId: PERMISSIONS_KEYS.access_laundrySettingsPrivileges,
          },
          {
            label: this.translations.orderRules(),
            icon: this.icons.listCheck,
            href: this.routerLinks.laundrySettingsOrderRules()[0],
            permissionId: PERMISSIONS_KEYS.access_laundrySettingsOrderRules,
          },
          {
            label: this.translations.workingHours(),
            icon: this.icons.clock,
            href: this.routerLinks.settingsWorkingHours()[0],
            permissionId: PERMISSIONS_KEYS.access_settingsWorkingHours,
          },
        ],
      },
      ...(!this.isProduction
        ? [
            {
              label: this.translations.loans(),
              icon: this.icons.buildingColumns,
              expanded: false,
              children: [
                {
                  label: this.translations.loanRequests(),
                  icon: this.icons.fileCheck,
                  href: this.routerLinks.loansLoanRequestsOverview()[0],
                  permissionId: '',
                },
                ...(userRole === ADMIN
                  ? [
                      {
                        label: this.translations.loanTypes(),
                        icon: this.icons.list,
                        href: this.routerLinks.loansLoanManagementLoanTypes()[0],
                        permissionId: '',
                      },
                      {
                        label: this.translations.loanCategories(),
                        icon: this.icons.folder,
                        href: this.routerLinks.loansLoanManagementLoanCategories()[0],
                        permissionId: '',
                      },
                      {
                        label: this.translations.attachments(),
                        icon: this.icons.paperclip,
                        href: this.routerLinks.loansLoanManagementAttachments()[0],
                        permissionId: '',
                      },
                      {
                        label: this.translations.deductionEntities(),
                        icon: this.icons.buildingColumns,
                        href: this.routerLinks.loansDeductionEntities()[0],
                        permissionId: '',
                      },
                      {
                        label: this.translations.insurances(),
                        icon: this.icons.shield,
                        href: this.routerLinks.loansInsurances()[0],
                        permissionId: '',
                      },
                    ]
                  : []),
                {
                  label: this.translations.committees(),
                  icon: this.icons.users,
                  href: this.routerLinks.loansCommitteesOverview()[0],
                  permissionId: '',
                },
                ...(userRole === ADMIN
                  ? [
                      {
                        label: this.translations.committeesMembers(),
                        icon: this.icons.userPlus,
                        href: this.routerLinks.loansCommitteesCommitteesMembers()[0],
                        permissionId: '',
                      },
                    ]
                  : []),
                ...(userRole === ADMIN_STAFF
                  ? [
                      {
                        label: this.translations.committeesRequests(),
                        icon: this.icons.clipboard,
                        href: this.routerLinks.loansCommitteesCommitteesRequests()[0],
                        permissionId: '',
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(!this.isProduction
        ? [
            {
              label: this.translations.stores(),
              icon: this.icons.warehouse,
              expanded: false,
              children: [
                {
                  label: this.translations.stores(),
                  icon: this.icons.warehouse,
                  href: this.routerLinks.storesStores()[0],
                  permissionId: '',
                },
                {
                  label: this.translations.storeActivities(),
                  icon: this.icons.wavePulse,
                  href: this.routerLinks.storeActivitiesList()[0],
                  permissionId: '',
                },
                {
                  label: this.translations.reports(),
                  icon: this.icons.chartLine,
                  href: this.routerLinks.storesReports()[0],
                  permissionId: '',
                },
                {
                  label: this.translations.returnPolicy(),
                  icon: this.icons.replay,
                  href: this.routerLinks.storesSettingsReturnPolicy()[0],
                  permissionId: '',
                },
              ],
            },
          ]
        : []),
      {
        label: this.translations.contentAndSupport(),
        icon: this.icons.comments,
        expanded: false,
        children: [
          {
            label: this.translations.correspondences(),
            icon: this.icons.inbox,
            href: this.routerLinks.messages()[0],
            permissionId: PERMISSIONS_KEYS.access_messages,
          },
          {
            label: this.translations.news(),
            icon: this.icons.file,
            href: this.routerLinks.news()[0],
            permissionId: PERMISSIONS_KEYS.access_news,
          },
          {
            label: this.translations.announcements(),
            icon: this.icons.megaphone,
            href: this.routerLinks.announcements()[0],
            permissionId: PERMISSIONS_KEYS.access_announcements,
          },
          {
            label: this.translations.events(),
            icon: this.icons.calendar,
            href: this.routerLinks.events()[0],
            permissionId: PERMISSIONS_KEYS.access_events,
          },
        ],
      },
      {
        label: this.translations.administration(),
        icon: this.icons.cog,
        expanded: false,
        children: [
          {
            label: this.translations.roles(),
            icon: this.icons.lock,
            href: this.routerLinks.roles()[0],
            permissionId: PERMISSIONS_KEYS.access_roles,
          },
          {
            label: this.translations.staffAndPermissions(),
            icon: this.icons.userEdit,
            href: this.routerLinks.staff()[0],
            permissionId: PERMISSIONS_KEYS.access_staff,
          },
          {
            label: this.translations.cities(),
            icon: this.icons.map,
            href: this.routerLinks.settingsCities()[0],
            permissionId: PERMISSIONS_KEYS.access_settingsCities,
          },
          {
            label: this.translations.offerCategories(),
            icon: this.icons.tags,
            href: this.routerLinks.settingsOfferCategories()[0],
            permissionId: PERMISSIONS_KEYS.access_settingsOfferCategories,
          },
          {
            label: this.translations.pages(),
            icon: this.icons.file,
            href: this.routerLinks.settingsPages()[0],
            permissionId: PERMISSIONS_KEYS.access_settingsPages,
          },
          {
            label: this.translations.email(),
            icon: this.icons.envelope,
            href: this.routerLinks.settingsEmail()[0],
            permissionId: PERMISSIONS_KEYS.access_settingsEmail,
          },
          {
            label: this.translations.messageEntites(),
            icon: this.icons.comments,
            href: this.routerLinks.settingsMessageEntites()[0],
            permissionId: PERMISSIONS_KEYS.access_settingsMessageEntites,
          },
          {
            label: this.translations.paymentMethods(),
            icon: this.icons.creditCard,
            href: this.routerLinks.settingsPaymentMethods()[0],
            permissionId: PERMISSIONS_KEYS.access_settingsPaymentMethods,
          },
          ...(!this.isProduction
            ? [
                {
                  label: this.translations.editWallet(),
                  icon: this.icons.wallet,
                  href: this.routerLinks.settingsWalletSettingsEditWallet()[0],
                  permissionId:
                    PERMISSIONS_KEYS.access_settingsWalletSettingsEditWallet,
                },
                {
                  label: this.translations.walletCycle(),
                  icon: this.icons.sync,
                  href: this.routerLinks.settingsWalletSettingsWalletCycle()[0],
                  permissionId:
                    PERMISSIONS_KEYS.access_settingsWalletSettingsWalletCycle,
                },
                {
                  label: this.translations.financialReport(),
                  icon: this.icons.chartLine,
                  href: this.routerLinks.settingsWalletSettingsFinancialReport()[0],
                  permissionId:
                    PERMISSIONS_KEYS.access_settingsWalletSettingsFinancialReport,
                },
                {
                  label: this.translations.exportOperationsReport(),
                  icon: this.icons.fileExport,
                  href: this.routerLinks.settingsWalletSettingsExportOperationsReport()[0],
                  permissionId:
                    PERMISSIONS_KEYS.access_settingsWalletSettingsExportOperationsReport,
                },
                {
                  label: this.translations.paymentOperationsReport(),
                  icon: this.icons.receipt,
                  href: this.routerLinks.settingsWalletSettingsPaymentOperationsReport()[0],
                  permissionId:
                    PERMISSIONS_KEYS.access_settingsWalletSettingsPaymentOperationsReport,
                },
                {
                  label: this.translations.adminProcedures(),
                  icon: this.icons.slidersH,
                  href: this.routerLinks.settingsWalletSettingsAdminProcedures()[0],
                  permissionId:
                    PERMISSIONS_KEYS.access_settingsWalletSettingsAdminProcedures,
                },
                {
                  label: this.translations.beneficiariesInfoWallet(),
                  icon: this.icons.addressBook,
                  href: this.routerLinks.settingsWalletSettingsBeneficiariesInfoWallet()[0],
                  permissionId:
                    PERMISSIONS_KEYS.access_settingsWalletSettingsBeneficiariesInfoWallet,
                },
              ]
            : []),
        ],
      },
    ];
  }
}
